// src/pages/operations-dashboard/components/DashboardFilterBar.jsx
import React, { useState, useRef, useEffect } from "react";
import { CalendarDays, ChevronDown, RefreshCw, Filter, X, Download } from "lucide-react";
import { PERIOD_OPTIONS } from "../../../utils/dashboardConstants";

/**
 * Enterprise-grade filter bar for the Operations Dashboard.
 * Sticky at the top, provides period selection, custom date range, and refresh.
 */
export default function DashboardFilterBar({
  period,
  setPeriod,
  fromDateTime,
  setFromDateTime,
  toDateTime,
  setToDateTime,
  onRefresh,
  lastRefresh,
  isLoading,
}) {
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowPeriodDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handlePeriodChange = (value) => {
    setPeriod(value);
    setShowPeriodDropdown(false);
    if (value === "CUSTOM") {
      setShowCustomRange(true);
    } else {
      setShowCustomRange(false);
      setFromDateTime(null);
      setToDateTime(null);
    }
  };

  const currentLabel = PERIOD_OPTIONS.find((p) => p.value === period)?.label || "Select Period";

  const formatLastRefresh = () => {
    if (!lastRefresh) return "";
    const now = new Date();
    const diff = Math.round((now - lastRefresh) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return lastRefresh.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-gray-100 shadow-sm">
      <div className="px-6 py-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Left: Period Selector */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Period Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                className="flex items-center gap-2.5 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:border-blue-300 hover:shadow-md transition-all duration-200 min-w-[180px]"
              >
                <CalendarDays className="w-4 h-4 text-blue-500" />
                <span className="flex-1 text-left">{currentLabel}</span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showPeriodDropdown ? "rotate-180" : ""}`} />
              </button>

              {showPeriodDropdown && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-100 rounded-2xl shadow-2xl shadow-gray-200/50 py-2 z-50 animate-in fade-in slide-in-from-top-2">
                  {PERIOD_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handlePeriodChange(opt.value)}
                      className={`w-full px-4 py-2.5 text-left text-sm transition-all duration-150 flex items-center gap-2.5 ${
                        period === opt.value
                          ? "bg-blue-50 text-blue-700 font-semibold"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      }`}
                    >
                      {period === opt.value && (
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                      )}
                      <span className={period !== opt.value ? "ml-4" : ""}>{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Custom Date Range */}
            {(period === "CUSTOM" || showCustomRange) && (
              <div className="flex items-center gap-2 animate-in fade-in slide-in-from-left-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50/50 border border-blue-100 rounded-xl">
                  <input
                    type="datetime-local"
                    value={fromDateTime || ""}
                    onChange={(e) => setFromDateTime(e.target.value)}
                    className="bg-transparent text-sm text-gray-700 outline-none font-medium"
                    placeholder="From"
                  />
                  <span className="text-gray-400 text-xs font-medium">to</span>
                  <input
                    type="datetime-local"
                    value={toDateTime || ""}
                    onChange={(e) => setToDateTime(e.target.value)}
                    className="bg-transparent text-sm text-gray-700 outline-none font-medium"
                    placeholder="To"
                  />
                </div>
                <button
                  onClick={() => {
                    setShowCustomRange(false);
                    setPeriod("TODAY");
                    setFromDateTime(null);
                    setToDateTime(null);
                  }}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Quick Period Pills */}
            <div className="hidden xl:flex items-center gap-1.5 ml-2">
              {["TODAY", "LAST_7_DAYS", "LAST_30_DAYS", "THIS_MONTH"].map((p) => {
                const label = PERIOD_OPTIONS.find((o) => o.value === p)?.label;
                return (
                  <button
                    key={p}
                    onClick={() => handlePeriodChange(p)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all duration-200 ${
                      period === p
                        ? "bg-blue-500 text-white shadow-md shadow-blue-200"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-3">
            {/* Last refresh indicator */}
            {lastRefresh && (
              <span className="text-xs text-gray-400 font-medium hidden sm:block">
                Updated {formatLastRefresh()}
              </span>
            )}

            {/* Refresh Button */}
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:border-blue-300 hover:text-blue-600 hover:shadow-md disabled:opacity-50 transition-all duration-200"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
